import React, { useEffect, useRef, useState } from 'react';
import { companyStats } from '../data/siteData';
import Reveal from './Reveal';

const CountedValue = ({ value, prefix, suffix, active, delay }) => {
  const [display, setDisplay] = useState(value);
  const [counting, setCounting] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    let frameId;
    let delayId;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    const start = () => {
      if (reduced) {
        setDisplay(value);
        return;
      }

      setDisplay(0);
      setCounting(true);
      const startedAt = performance.now();
      const duration = 1850;
      const tick = (time) => {
        const progress = Math.min((time - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);
        setDisplay(Math.round(value * eased));
        if (progress < 1) {
          frameId = requestAnimationFrame(tick);
        } else {
          setDisplay(value);
          setCounting(false);
        }
      };
      frameId = requestAnimationFrame(tick);
    };

    delayId = window.setTimeout(start, delay);
    return () => {
      window.clearTimeout(delayId);
      cancelAnimationFrame(frameId);
    };
  }, [active, delay, value]);

  return (
    <strong
      className={`counted-value${counting ? ' is-counting' : ''}`}
      aria-label={`${prefix}${value}${suffix}`}
    >
      {prefix}
      {display}
      {suffix}
    </strong>
  );
};

const NumbersSection = () => {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || !('IntersectionObserver' in window)) {
      setActive(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.22, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="numbers section" aria-labelledby="numbers-title">
      <div className="numbers__backdrop" aria-hidden="true">RYU</div>
      <div className="container">
        <Reveal className="numbers__heading">
          <p className="eyebrow">RYU IN NUMBERS</p>
          <h2 id="numbers-title">積み重ねた信頼を、次の成長へ。</h2>
        </Reveal>
        <dl className="numbers__grid">
          {companyStats.map((stat, index) => (
            <Reveal as="div" className="number-item" delay={index * 90} key={stat.label}>
              <dt>
                <CountedValue
                  value={stat.value}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  active={active}
                  delay={360 + index * 130}
                />
                <span>{stat.label}</span>
              </dt>
              <dd>{stat.note}</dd>
            </Reveal>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default NumbersSection;
